import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Kicker, SectionHeader, Stat } from "@/components/Bits";
import { CtaRow, BookDirect, CheckAvailBtn } from "@/components/CTAButtons";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { ParallaxImage } from "@/components/ParallaxImage";
import { FaqList } from "@/components/FaqList";
import { maskChild, staggerParent, scaleIn } from "@/lib/motion";
import { HOTEL, IMG, ROOMS, ATTRACTIONS, REVIEWS, FAQS, MANIFESTO } from "@/data/site";
import { Waves, Coffee, Wifi, Truck, WashingMachine, Clock, ArrowRight, ArrowUpRight, MapPin, Quote, Trees, GraduationCap, Building2, Landmark, Fish, ShoppingBag, Ticket } from "lucide-react";

const HERO_LINES = ["The best rest", "in Ottawa,", "Kansas."];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-end overflow-hidden bg-charcoal">
      <motion.div style={{ y, scale }} className="absolute inset-0" variants={scaleIn} initial="hidden" animate="show">
        <img src={IMG.exterior} alt="Super 8 by Wyndham Ottawa hotel exterior off I-35 in Ottawa, Kansas" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
      </motion.div>

      <div className="relative mx-auto w-full max-w-[1400px] px-5 pb-28 pt-32 md:px-10 md:pb-32">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Kicker tone="yellow">Super 8 by Wyndham · Right off I-35</Kicker>
        </motion.div>

        <motion.h1 variants={staggerParent} initial="hidden" animate="show" className="mt-6 font-display text-[16vw] uppercase leading-[0.82] tracking-tight text-white sm:text-8xl lg:text-[9rem]">
          {HERO_LINES.map((line, i) => (
            <span key={i} className="mask-reveal">
              <motion.span variants={maskChild} custom={i} className="block">
                {i === 2 ? <span className="text-brand-yellow">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.6 }} className="mt-8 max-w-xl font-body text-base leading-relaxed text-white/85 md:text-lg">
          Clean, comfortable rooms · free SuperStart breakfast · free WiFi · a year-round indoor pool
          and free large-vehicle parking. Book direct for the best available rate.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.6 }} className="mt-9">
          <CtaRow onDark />
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.6 }} className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3">
          <span className="font-body text-sm text-white/80">Loved by families, road-trippers & truck drivers</span>
          {["Free Breakfast", "Free WiFi", "Indoor Pool", "Truck Parking"].map((t) => (
            <span key={t} className="font-body text-xs font-semibold uppercase tracking-wide text-brand-yellow">✳ {t}</span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const bentoItems = [
  { name: "Indoor Heated Pool", icon: Waves, image: IMG.pool, span: "md:col-span-2 md:row-span-2", to: "/indoor-pool", tag: "Year-round" },
  { name: "Free SuperStart Breakfast", icon: Coffee, image: IMG.breakfast, span: "", to: "/amenities" },
  { name: "Free High-Speed WiFi", icon: Wifi, image: null, span: "", to: "/amenities" },
  { name: "Truck · RV · Bus Parking", icon: Truck, image: IMG.truck, span: "md:col-span-2", to: "/truck-parking", tag: "Free & oversized" },
  { name: "Guest Laundry", icon: WashingMachine, image: null, span: "", to: "/extended-stay" },
  { name: "24-Hour Front Desk", icon: Clock, image: null, span: "", to: "/amenities" },
];

const ATTR_ICON = {
  Outdoors: Trees, University: GraduationCap, "Shopping & Dining": ShoppingBag,
  History: Landmark, "Lake & Fishing": Fish, Parks: Trees, Shopping: ShoppingBag,
  Events: Ticket,
};

export default function Home() {
  return (
    <>
      <Seo
        title="Hotel in Ottawa, KS — Indoor Pool, Free Breakfast, Truck Parking"
        description="Book the Super 8 by Wyndham Ottawa direct for the best rate. Hotel off I-35 in Ottawa, Kansas with a year-round indoor pool, free SuperStart breakfast, free WiFi and free truck & RV parking near Ottawa University and the Prairie Spirit Trail."
        path="/"
        image={IMG.exterior}
        faqs={FAQS}
      />

      <Hero />
      <EditorialMarquee />

      {/* MANIFESTO */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
        <SectionHeader kicker="Reasons to Stay" title="Four reasons guests keep coming back" />
        <div className="mt-16 divide-y divide-charcoal/10 border-y border-charcoal/10">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} i={i} className="grid gap-6 py-10 md:grid-cols-12 md:items-baseline md:py-14">
              <span className="font-display text-6xl leading-none text-brand-red md:col-span-2 md:text-7xl">{m.n}</span>
              <h3 className="font-heading text-2xl font-black text-charcoal md:col-span-4 md:text-3xl">{m.title}</h3>
              <p className="font-body text-base leading-relaxed text-fog md:col-span-6">{m.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* AMENITIES BENTO */}
      <section className="border-t border-charcoal/10 bg-bone py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHeader kicker="Amenities" title="Everything on the house" sub="Free breakfast, free WiFi, an indoor pool and parking big enough for anything you drive — the essentials, done consistently well." />
          <div className="mt-14 grid auto-rows-[220px] grid-cols-1 gap-3 md:grid-cols-4">
            {bentoItems.map((b, i) => {
              const Icon = b.icon;
              const dark = !!b.image;
              return (
                <Reveal key={b.name} i={i % 3} className={b.span}>
                  <Link to={b.to} data-testid={`bento-${i}`} className={`group relative flex h-full flex-col justify-between overflow-hidden border-2 border-charcoal p-6 transition-colors duration-300 ${dark ? "bg-charcoal" : "bg-paper hover:bg-brand-yellow"}`}>
                    {b.image && (
                      <>
                        <img src={b.image} alt={b.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-70 transition-all duration-500 group-hover:scale-105 group-hover:opacity-80" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                      </>
                    )}
                    <div className="relative flex items-start justify-between">
                      <Icon className={`h-7 w-7 ${dark ? "text-brand-yellow" : "text-brand-red"}`} />
                      {b.tag && <span className={`border px-2 py-1 font-body text-[10px] font-semibold uppercase tracking-wide ${dark ? "border-white/30 text-white" : "border-charcoal/30 text-charcoal"}`}>{b.tag}</span>}
                    </div>
                    <div className="relative">
                      <h3 className={`font-heading text-xl font-black ${dark ? "text-white" : "text-charcoal"}`}>{b.name}</h3>
                      <span className={`mt-2 inline-flex items-center gap-1 font-body text-xs font-semibold uppercase tracking-wide opacity-0 transition-opacity duration-300 group-hover:opacity-100 ${dark ? "text-brand-yellow" : "text-brand-red"}`}>
                        Explore <ArrowUpRight className="h-3 w-3" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-8">
            <Link to="/amenities" data-testid="all-amenities-link" className="inline-flex items-center gap-2 font-heading font-black uppercase text-charcoal transition-colors hover:text-brand-red">
              All amenities <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ROOMS TEASER */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHeader kicker="Rooms & Suites" title="Room to breathe" sub="From king rooms to double queens, jacuzzi suites and accessible rooms — pick the space that fits your trip." />
          <Link to="/rooms" data-testid="rooms-teaser-link" className="hidden shrink-0 items-center gap-2 font-heading font-black uppercase text-charcoal transition-colors hover:text-brand-red md:inline-flex">
            View all rooms <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-14 grid gap-3 md:grid-cols-3">
          {ROOMS.slice(0, 3).map((r, i) => (
            <Reveal key={r.slug} i={i}>
              <Link to={`/rooms/${r.slug}`} data-testid={`room-teaser-${r.slug}`} className="group block">
                <div className="relative aspect-[4/5] overflow-hidden border-2 border-charcoal">
                  <img src={r.image} alt={`${r.name} at ${HOTEL.name}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6">
                    <p className="font-body text-xs font-semibold uppercase tracking-wide text-brand-yellow">{r.beds} · {r.sleeps}</p>
                    <h3 className="mt-1 font-heading text-2xl font-black text-white">{r.name}</h3>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INDOOR POOL FEATURE */}
      <section className="grid items-stretch lg:grid-cols-2">
        <ParallaxImage src={IMG.poolKids} alt="Family enjoying the indoor heated pool at Super 8 Ottawa" height="min-h-[60vh] h-full" strength={40} className="border-0" />
        <div className="flex flex-col justify-center bg-brand-yellow px-6 py-20 md:px-16">
          <span className="font-body text-xs font-semibold uppercase tracking-[0.35em] text-brand-red">Major reason to book</span>
          <h2 className="mt-4 font-display text-5xl uppercase leading-[0.9] tracking-tight text-charcoal md:text-7xl">A heated indoor pool, open all year</h2>
          <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-charcoal/80">
            Rain, snow or shine — the kids can swim and you can unwind any day of the year. It's the
            perfect antidote to a long drive on I-35 and the reason families choose us for weekend getaways.
          </p>
          <div className="mt-8">
            <Link to="/indoor-pool" data-testid="pool-feature-link" className="inline-flex items-center gap-2 bg-charcoal px-6 py-3 font-heading font-black uppercase text-brand-yellow transition-colors duration-300 hover:bg-brand-red hover:text-white">
              Explore the pool <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-charcoal/10 bg-paper py-20">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-10 px-5 md:grid-cols-4 md:px-10">
          {[
            { value: "24/7", label: "Front desk" },
            { value: "$0", label: "Breakfast & WiFi" },
            { value: "I-35", label: "Right off the highway" },
            { value: "6", label: "Room types" },
          ].map((s, i) => (
            <Reveal key={s.label} i={i}><Stat value={s.value} label={s.label} /></Reveal>
          ))}
        </div>
      </section>

      {/* ATTRACTIONS (no location photos — clean editorial cards) */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHeader kicker="The Area" title="Adventure awaits nearby" sub="Universities, lakes, trails and a historic downtown — all within easy reach of your room." />
          <Link to="/attractions" data-testid="attractions-teaser-link" className="hidden shrink-0 items-center gap-2 font-heading font-black uppercase text-charcoal transition-colors hover:text-brand-red md:inline-flex">
            All attractions <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {ATTRACTIONS.slice(0, 4).map((a, i) => {
            const Icon = ATTR_ICON[a.cat] || Building2;
            return (
              <Reveal key={a.name} i={i}>
                <div className="group flex h-full flex-col justify-between border-2 border-charcoal bg-paper p-7 transition-colors duration-300 hover:bg-brand-yellow">
                  <div className="flex items-start justify-between">
                    <Icon className="h-8 w-8 text-brand-red" />
                    <span className="font-body text-xs font-semibold uppercase tracking-wide text-charcoal/60">{a.dist}</span>
                  </div>
                  <div className="mt-10">
                    <p className="font-body text-[11px] font-semibold uppercase tracking-wide text-brand-red">{a.cat}</p>
                    <h3 className="mt-1 font-heading text-lg font-black leading-tight text-charcoal">{a.name}</h3>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="border-t border-charcoal/10 bg-bone py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHeader kicker="Guest Reviews" title="Loved by travelers" sub="Real words from real guests — families, road-trippers and truck drivers alike." />
          <div className="mt-14 grid gap-3 md:grid-cols-3">
            {REVIEWS.slice(0, 6).map((r, i) => (
              <Reveal key={i} i={i % 3} className="flex flex-col justify-between border-2 border-charcoal bg-paper p-8">
                <div>
                  <Quote className="h-8 w-8 text-brand-red" />
                  <p className="mt-5 font-body text-base leading-relaxed text-charcoal/90">"{r.text}"</p>
                </div>
                <div className="mt-8">
                  <p className="font-heading font-black text-charcoal">{r.name}</p>
                  <p className="font-body text-sm text-fog">{r.place}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5">
            <SectionHeader kicker="Find Us" title="Right off I-35" />
            <Reveal i={2} className="mt-6 space-y-3 font-body text-charcoal/80">
              <p className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 text-brand-red" /> {HOTEL.fullAddress}</p>
              <p>Minutes from Ottawa University, the Prairie Spirit Trail and historic downtown Ottawa — and an easy drive to Lawrence and the University of Kansas.</p>
            </Reveal>
            <Reveal i={3} className="mt-8 flex flex-wrap gap-3">
              <BookDirect />
              <CheckAvailBtn />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal className="border-2 border-charcoal">
              <iframe
                title="Map to Super 8 by Wyndham Ottawa"
                src={HOTEL.mapEmbed}
                className="h-[420px] w-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ TEASER */}
      <section className="mx-auto max-w-4xl px-5 pb-24 md:px-10 md:pb-32">
        <SectionHeader kicker="Questions?" title="Quick answers" />
        <div className="mt-12">
          <FaqList faqs={FAQS.slice(0, 8)} testidPrefix="home-faq" />
        </div>
        <Reveal className="mt-10">
          <Link to="/faq" data-testid="all-faq-link" className="inline-flex items-center gap-2 font-heading font-black uppercase text-charcoal transition-colors hover:text-brand-red">
            See all FAQs <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
